import { useEffect, useMemo, useRef, useState } from 'react';
import { LoadingScreen } from 'components/screens';
import { getLocaleLanguage, getStoredLocale, initIntl } from 'modules/i18n';
import { logError } from 'utils/datadog';
import { unsupportedBlocksOf } from './blocks/registry';
import { FormRunner } from './FormRunner';
import { useGuestFormSession } from './GuestFormSessionContext';
import { GuestFormStatusScreen } from './GuestFormStatusScreen';
import { preferredLocale } from './localizedText';

/**
 * Chooses between the runner and a status screen, and enforces one rule before rendering anything:
 * a form containing a block this build cannot render is not shown at all.
 *
 * Skipping the unknown block would be worse than refusing. The respondent would answer what is
 * left, submit what looks like a finished form, and nobody — not them, not the operator reading the
 * responses — would know a question had silently disappeared. So the whole form gives way to
 * "update your app", which is recoverable, honest, and loud in Error Tracking (catalog §15).
 */
export const GuestFormFlow = () => {
  const { state, data, markNeedsUpdate } = useGuestFormSession();

  const unsupported = useMemo(
    () => unsupportedBlocksOf(data?.definition ?? null),
    [data]
  );
  const hasUnsupported = state === 'active' && unsupported.length > 0;
  const reportedRef = useRef(false);

  useEffect(() => {
    if (!hasUnsupported || reportedRef.current) {
      return;
    }
    reportedRef.current = true;
    // An error rather than an action: it means a form was published against a block version this
    // deployed app cannot render, which is a release-ordering problem to fix — not a normal outcome
    // of somebody's session.
    logError('Guest form contains unrenderable blocks', undefined, {
      feature: 'guest-forms',
      types: unsupported
        .map(block => `${block.type}@${block.typeVersion}`)
        .join(',')
    });
    markNeedsUpdate();
  }, [hasUnsupported, unsupported, markNeedsUpdate]);

  /*
   * Open the form in the language the reader is most likely to want, before they see anything.
   *
   * The app has never done this: `getCurrentLocale` falls back to German for anybody who has never
   * chosen, so an English phone opening a bilingual form got German questions and German buttons.
   * `preferredLocale` puts an explicit choice first, then the device, then the operator's first
   * language — see it for why that order.
   *
   * Deliberately NOT persisted. Re-initialising the bundles switches this page load, which is what
   * makes the questions and the chrome agree; writing it to storage would silently change the app's
   * language for a talent who merely followed a form link. The flag in the runner's bar persists,
   * because that IS somebody choosing.
   */
  const desiredLocale = useMemo(
    () => preferredLocale(data?.definition ?? null),
    [data]
  );
  /*
    Which locale the bundles have been loaded FOR, rather than a "done" flag.

    A boolean was wrong in the one sequence that matters. The provider mounts with `state: loading`
    and no data, so the first render has no definition, `preferredLocale` answers with the language
    already loaded, and a "have we applied it?" flag initialises TRUE. The definition then arrives
    with a different answer — and the effect, seeing the flag already set, bailed and never loaded
    anything. The form rendered in the wrong language and nothing looked broken.

    Keyed by locale, a new answer is simply an answer nothing has been loaded for yet.
  */
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const localeReady =
    desiredLocale === (getLocaleLanguage() as string) ||
    loadedFor === desiredLocale;

  // Whatever was loaded before this component touched anything. `useRef`'s initializer only takes
  // on the first render, so this is a snapshot of "on the way in" — the thing to put back on the
  // way out.
  const originalLocaleRef = useRef(getLocaleLanguage() as string);
  // The forward switch's own promise, so the unmount restore below can wait for it rather than
  // race it — `initIntl` mutates the shared `react-intl-universal` singleton the moment IT resolves,
  // regardless of whether this component is still mounted to care.
  const pendingSwitchRef = useRef<Promise<unknown> | null>(null);

  useEffect(() => {
    /*
      Whenever there is a definition to read a language OUT of — not only while the form is being
      answered.

      Scoping this to `active` left one visible hole: somebody who answered an English form and later
      reopened the link got a German "Danke — deine Antworten sind da". By then the invitation is
      consumed, so there is nothing to answer and the language step was skipped, falling back to the
      app default. The screen is short, but it is the last thing that person sees and it contradicts
      the form they just filled in.

      `data` is what gates it now: the redeem payload carries the definition for a submitted
      invitation too. A dead end with no payload at all — a bad token, no token — still renders in
      the app's own language, because there is no form to take one from.
    */
    if (!data || hasUnsupported || localeReady) {
      return;
    }
    let abandoned = false;
    const switching = initIntl(desiredLocale)
      .catch(error =>
        // Loading a language is not worth a dead end. The questions still render — from the
        // definition, in the right language — and only the words around them stay as they were.
        logError('Guest form locale bundles failed to load', error, {
          feature: 'guest-forms',
          locale: desiredLocale
        })
      )
      .finally(() => {
        // `finally`, not `then`: a rejection that left this unset would hold the form on its loading
        // screen for good, which is worse than any language.
        if (!abandoned) {
          setLoadedFor(desiredLocale);
        }
      });
    // Recorded so the unmount restore below can wait its turn instead of losing a race to it.
    pendingSwitchRef.current = switching;
    return () => {
      abandoned = true;
    };
  }, [data, hasUnsupported, desiredLocale, localeReady]);

  useEffect(
    () => () => {
      /*
       * Undo an automatic pick on the way out — but only an automatic one.
       *
       * This route's own comment above is explicit that the switch is "deliberately NOT persisted":
       * re-initialising the bundles is meant to affect this page load, not the app. But `initIntl`
       * has no notion of "for this route only" — it repoints the app-wide `react-intl-universal`
       * singleton, and that repoint outlives the component. A talent who stays logged in and merely
       * followed a form link would carry the guessed device locale into every other screen.
       *
       * Waiting for `pendingSwitchRef` first closes the one race that matters here: leaving before
       * the forward switch has even resolved. `initIntl` mutates the singleton the moment ITS OWN
       * promise settles, with no regard for whether this effect's cleanup already ran, so restoring
       * before that lands would just be overwritten a moment later.
       *
       * `getStoredLocale` is the one check that keeps this from clobbering a real decision: someone
       * who used the flag on this very form has their choice persisted through it, and that is a
       * decision, not a guess, so it must survive navigating away — same as it survives the reload
       * the flag itself causes.
       */
      void Promise.resolve(pendingSwitchRef.current).finally(() => {
        const original = originalLocaleRef.current;
        const current = getLocaleLanguage() as string;
        const stored = getStoredLocale() as string | null;
        if (current !== original && stored !== current) {
          void initIntl(original);
        }
      });
    },
    []
  );

  if (state === 'loading') {
    return <LoadingScreen />;
  }

  if (hasUnsupported) {
    return <GuestFormStatusScreen state="needs-update" />;
  }

  /*
    A status screen is NOT held back while the bundles load.

    The runner is, because a question rendering in one language and re-rendering in another a frame
    later is the flicker this whole thing exists to avoid. A single line of thanks is not worth a
    loading screen — it appears immediately, in whatever language is loaded, and settles into the
    form's own a moment later if that differs.
  */

  // Holding the runner back until the bundles are ready is what stops the first question rendering
  // in one language and re-rendering in another a frame later.
  if (state === 'active' && data && !localeReady) {
    return <LoadingScreen />;
  }

  if (state === 'active' && data) {
    return (
      /*
       * Deliberately NOT inside `Layout`.
       *
       * The runner is its own frame — three fixed regions over a full viewport height, with the
       * safe-area insets applied at its own edges. `Layout` wraps its children in a `Container` with
       * gutters and bottom padding, which on a phone would inset the frame from the screen edges and
       * put the action bar above the home indicator rather than clear of it. Its page transition
       * would also run on top of the runner's own screen transitions.
       */
      <FormRunner data={data} />
    );
  }

  // `active` without data cannot happen — the provider sets both in one go — but the fallback has
  // to be a screen rather than a crash.
  return (
    <GuestFormStatusScreen state={state === 'active' ? 'invalid' : state} />
  );
};
